import 'd3-transition'

import screenfull from 'screenfull'
import { clamp } from '@yuntu/shared'
import type { Selection } from 'd3-selection'
import { select } from 'd3-selection'
import type { ZoomBehavior } from 'd3-zoom'
import { zoom, zoomIdentity } from 'd3-zoom'

import type { Graph, Node } from '../entities'
import { EVENT_NAME, GraphEventType, LayoutConfig } from '../types'
import { calcTransformForBounds } from '../utils'

export interface ViewportOptions {
  /**
   * 缩放最小比例
   */
  minZoom?: number
  /**
   * 缩放最大比例
   */
  maxZoom?: number
  /**
   * 默认缩放比例
   */
  defaultZoom?: number
  /**
   * 默认画布偏移量
   */
  defaultPosition?: [number, number]
  /**
   * 最大画布偏移量
   */
  translateExtent?: [[number, number], [number, number]]
  /**
   * 启用自适应
   */
  fitView?: boolean
  /**
   * 自适应画布间距
   */
  fitViewPadding?: [number, number]
  /**
   * 中心对齐
   */
  fitCenter?: boolean
  /**
   * 禁止滚动
   */
  preventScrolling?: boolean
  /**
   * 禁用缩放
   */
  disableZoom?: boolean
}

export interface ActionOptions {
  animate?: boolean
}

export class Viewport {
  get actionOptions(): Required<ActionOptions> {
    const { animate } = this.graph

    return {
      animate,
    }
  }

  name = 'viewport'
  graph: Graph
  /**
   * 上个布局类型
   */
  prevLayoutType?: LayoutConfig['type']
  /**
   * 用户是否拖放过画布
   */
  userModified = false
  /**
   * 插件配置
   */
  options: Omit<Required<ViewportOptions>, 'defaultPosition'> & {
    defaultPosition?: [number, number]
  }
  /**
   * d3 zoom 实例
   */
  d3Zoom?: ZoomBehavior<Element, unknown>
  /**
   * d3 zoom selection
   */
  d3ZoomSelection?: Selection<Element, unknown, null, undefined>

  constructor(graph: Graph, options: ViewportOptions) {
    const {
      minZoom = 0.2,
      maxZoom = 2,
      defaultZoom = 1,
      fitViewPadding = [0.5, 0.5],
      translateExtent = [
        [Number.NEGATIVE_INFINITY, Number.NEGATIVE_INFINITY],
        [Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY],
      ],
      defaultPosition,
      preventScrolling = true,
      fitView = false,
      fitCenter = true,
      disableZoom = false,
    } = options

    this.options = {
      minZoom,
      maxZoom,
      defaultZoom,
      fitViewPadding,
      defaultPosition,
      translateExtent,
      preventScrolling,
      fitView,
      fitCenter,
      disableZoom,
    }
    this.graph = graph

    this.graph.once(EVENT_NAME.afterinit, () => {
      // 初始化缩放控制
      this.initZoom()
    })
    this.graph.on(EVENT_NAME.beforelayout, () => {
      // 切换布局类型时，重置用户拖放标志位
      if (this.prevLayoutType !== this.graph.layoutType) {
        this.userModified = false
      }

      if (!this.userModified) {
        // 增加一个延时等待 DOM 变化
        setTimeout(() => {
          this.fitViewport({
            animate: this.graph.graphType === 'normal',
          })
        }, 300)
      }

      this.prevLayoutType = this.graph.layoutType
    })
    this.graph.on(EVENT_NAME.afterlayout, () => {
      // 用户已改变画布位置，不再自动调整
      if (this.userModified) {
        return
      }

      this.graph.once(EVENT_NAME.afterrender, () => {
        this.fitViewport({
          animate: this.graph.graphType === 'normal',
        })
      })
    })
  }

  /**
   * 获取当前视口的缩放比例
   */
  getZoom = () => this.graph.transform[2]

  /**
   * 缩放到指定比例，并以指定位置为中心
   *
   * @param zoom
   * @param center
   * @param actionOptions
   */
  zoomTo = (
    zoom: number,
    center: {
      x: number
      y: number
    },
    actionOptions = this.actionOptions
  ) => {
    const { width, height } = this.graph
    const { maxZoom } = this.options
    const nextZoom = !zoom ? zoom : maxZoom
    const centerX = width / 2 - center.x * nextZoom
    const centerY = height / 2 - center.y * nextZoom

    this.setTransform(centerX, centerY, nextZoom, actionOptions)
  }

  zoom = (nextZoom: number) => {
    const [translateX, translateY] = this.graph.transform
    this.setTransform(translateX, translateY, nextZoom)
  }

  /**
   * 放大
   *
   * @param animate 是否启用动画
   */
  zoomIn = ({ animate } = this.actionOptions) => {
    if (this.d3Zoom) {
      const selection = this.getD3Selection(animate)

      if (selection) {
        this.d3Zoom.scaleBy(selection, 1.2)
      }
    }
  }

  /**
   * 缩小
   *
   * @param animate 是否动画
   */
  zoomOut = ({ animate } = this.actionOptions) => {
    if (this.d3Zoom) {
      const selection = this.getD3Selection(animate)

      if (selection) {
        this.d3Zoom.scaleBy(selection, 1 / 1.2)
      }
    }
  }

  /**
   * 平移画布
   *
   * @param dx
   * @param dy
   * @param actionOptions
   */
  translate = (dx: number, dy: number, actionOptions = this.actionOptions) => {
    this.setTransform(dx, dy, 1, { animate: actionOptions.animate })
  }

  /**
   * 设置画布偏移
   *
   * @param translateX
   * @param translateY
   * @param zoom
   * @param animate
   */
  setTransform = (
    translateX: number,
    translateY: number,
    zoom = 1,
    { animate }: ActionOptions = this.actionOptions
  ) => {
    if (this.d3Zoom) {
      const selection = this.getD3Selection(animate)

      if (selection) {
        this.d3Zoom?.transform(
          selection,
          zoomIdentity.translate(translateX, translateY).scale(zoom)
        )
      }
    }
  }

  /**
   * 根据配置居中或者自适应
   *
   * @param actionOptions
   */
  fitViewport(actionOptions: ActionOptions = this.actionOptions) {
    if (this.graph.destroyed) {
      return
    }

    if (this.options.fitView) {
      this.fitView(actionOptions)
    } else if (this.options.fitCenter) {
      this.fitCenter(actionOptions)
    }
  }

  /**
   * 自适应
   */
  fitView = (actionOptions: ActionOptions = this.actionOptions) => {
    const [x, y, zoom] = this.calcFitViewTransform()

    this.setTransform(x, y, zoom, actionOptions)
  }

  /**
   * 居中
   */
  fitCenter = (actionOptions: ActionOptions = this.actionOptions) => {
    const { width, height, renderer } = this.graph
    const box = renderer.getBBox()
    const boundsCenterX = box.x + box.width / 2
    const boundsCenterY = box.y + box.height / 2
    const centerX = width / 2 - boundsCenterX
    const centerY = height / 2 - boundsCenterY

    this.setTransform(centerX, centerY, 1, actionOptions)
  }

  /**
   * 聚焦节点
   *
   * @param nodeId
   * @param actionOptions
   */
  focusNode(nodeId?: Node['id'], actionOptions = this.actionOptions) {
    if (nodeId == null) {
      return
    }

    const { width, height } = this.graph

    if (width && height) {
      const node = this.graph.findNodeById(nodeId)

      if (node) {
        const translateX = width / 2 - node.x
        const translateY = height / 2 - node.y

        this.setTransform(translateX, translateY, 1, actionOptions)
      }
    }
  }

  /**
   * 使用数据推断画布偏移量，碰到使用圆形时判断会不准确
   */
  calcCenterTransform = (): [number, number] => {
    const { width, height, renderer } = this.graph
    const box = renderer.getBBox()
    const boundsCenterX = box.x + box.width / 2
    const boundsCenterY = box.y + box.height / 2
    const centerX = width / 2 - boundsCenterX
    const centerY = height / 2 - boundsCenterY

    return [centerX, centerY]
  }

  /**
   * 获取自适应的画布偏移量
   */
  calcFitViewTransform = (): [number, number, number] => {
    const { width, height, renderer } = this.graph
    const { fitViewPadding, minZoom, maxZoom } = this.options
    const box = renderer.getBBox()
    const [x, y, zoom] = calcTransformForBounds(
      box,
      width,
      height,
      minZoom,
      maxZoom,
      fitViewPadding
    )

    return [x, y, zoom]
  }

  /**
   * 切换全屏
   */
  toggleFullScreen = () => {
    if (screenfull.isEnabled) {
      void screenfull.toggle(this.graph.renderer.container)
    }
  }

  /**
   * d3-selection
   *
   * @param animate
   */
  private getD3Selection = (animate = this.actionOptions.animate) => {
    if (this.d3ZoomSelection) {
      return animate
        ? this.d3ZoomSelection
            .transition()
            .duration(this.graph.animateOptions.duration)
        : this.d3ZoomSelection
    }

    return
  }

  /**
   * 计算默认偏移量
   */
  private calcDefaultTransform = () => {
    let x = 0
    let y = 0
    let zoom = 1

    if (this.options.defaultPosition) {
      x = this.options.defaultPosition[0]
      y = this.options.defaultPosition[1]
    }

    if (this.options.defaultZoom) {
      zoom = this.options.defaultZoom
    }

    return [x, y, zoom]
  }

  /**
   * 初始化缩放
   */
  private initZoom() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this
    const d3ZoomInstance = zoom()
      .scaleExtent([this.options.minZoom, this.options.maxZoom])
      .wheelDelta((event) => {
        return (-event.deltaY * (event.deltaMode ? 120 : 1)) / 150
      })
    const selection = select<Element, unknown>(
      this.graph.renderer.container
    ).call(d3ZoomInstance)
    const [defaultPositionX, defaultPositionY, defaultZoom] =
      this.calcDefaultTransform()
    const clampedX = clamp(
      defaultPositionX,
      this.options.translateExtent[0][0],
      this.options.translateExtent[1][0]
    )
    const clampedY = clamp(
      defaultPositionY,
      this.options.translateExtent[0][1],
      this.options.translateExtent[1][1]
    )
    const clampedZoom = clamp(
      defaultZoom,
      this.options.minZoom,
      this.options.maxZoom
    )
    const updatedTransform = zoomIdentity
      .translate(clampedX, clampedY)
      .scale(clampedZoom)
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const zoomHandler = selection.on('wheel.zoom')!

    this.d3Zoom = d3ZoomInstance
    this.d3ZoomSelection = selection

    that.graph.updateTransform([clampedX, clampedY, clampedZoom])

    d3ZoomInstance.transform(selection, updatedTransform)
    d3ZoomInstance
      .on('zoom', (event) => {
        const nextTransform: [number, number, number] = [
          event.transform.x,
          event.transform.y,
          event.transform.k,
        ]

        if (nextTransform.every((position) => !isNaN(position))) {
          that.graph.updateTransform(nextTransform)
        }

        if (event.sourceEvent?.type === 'mousemove') {
          this.graph.emitElementEvent(EVENT_NAME.drag, event.sourceEvent)
        }
      })
      .on('start', (event) => {
        if (event.sourceEvent?.type === 'mousedown') {
          this.graph.emitElementEvent(EVENT_NAME.dragstart, event.sourceEvent)
        }
      })
      .on('end', (event) => {
        if (event.sourceEvent?.type === 'mouseup') {
          this.graph.emitElementEvent(EVENT_NAME.dragend, event.sourceEvent)
        }
      })

    d3ZoomInstance.filter((event) => {
      const eventInfo = that.graph.detectElementEvent(event, EVENT_NAME.wheel)

      if (
        eventInfo == null ||
        eventInfo.type === GraphEventType.node ||
        eventInfo.type === GraphEventType.edge
      ) {
        return false
      }

      if (
        this.options.disableZoom &&
        (event.type === 'wheel' || event.type === 'dblclick')
      ) {
        return false
      }

      this.userModified = true

      // default filter for d3-zoom
      return (!event.ctrlKey || event.type === 'wheel') && !event.button
    })

    selection
      .on('wheel', (event) => {
        if (!that.options.preventScrolling) {
          return
        }

        event.preventDefault()
      })
      .on('wheel.zoom', zoomHandler)
      .on('mousedown', (event) => {
        event.preventDefault()
      })
  }
}
